import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  ChevronDown,
  CircleHelp,
  Clock3,
  FileBarChart,
  Home,
  LogOut,
  MapPin,
  Menu,
  MessageSquare,
  MonitorSmartphone,
  Play,
  Settings,
  ShieldCheck,
  Send,
  Square,
  Tags,
  UserRound,
  UsersRound,
  X
} from "lucide-react";
import type { BootstrapData } from "@czasomat/contracts";
import { api, post } from "../api/client";
import { Avatar, IconButton, cx } from "./ui";
import { useSelfPunch } from "../use-self-punch";

const topLinks = [
  { to: "/app", label: "Pulpit", icon: Home },
  { to: "/zespol", label: "Zespół", icon: UsersRound },
  { to: "/grafik-pracy", label: "Grafik", icon: CalendarDays },
  { to: "/wnioski-urlopowe", label: "Wnioski", icon: FileBarChart },
  { to: "/raporty/lista-obecnosci", label: "Raporty", icon: FileBarChart }
];

const rcpLinks = [
  { to: "/rcp/zdarzenia", label: "Zdarzenia", icon: Clock3 },
  { to: "/rcp/statusy-pracy", label: "Statusy pracy", icon: Tags },
  { to: "/rcp/lokalizacje", label: "Lokalizacje", icon: MapPin },
  { to: "/rcp/urzadzenia", label: "Urządzenia", icon: MonitorSmartphone },
  { to: "/kiosk", label: "Tryb kiosku", icon: ShieldCheck }
];

function useOutsideClose(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);
  return ref;
}

export function AppShell({ data, children }: { data: BootstrapData; children: ReactNode }) {
  const [rcpOpen, setRcpOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatBody, setChatBody] = useState("");
  const [chatRecipient, setChatRecipient] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const rcpRef = useOutsideClose(rcpOpen, () => setRcpOpen(false));
  const profileRef = useOutsideClose(profileOpen, () => setProfileOpen(false));
  const virtualPunch = useSelfPunch(data);
  const isWorking = data.selfPunch?.nextType === "WORK_STOP";
  const workingSince = data.selfPunch?.workingSince;
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const calculate = () => {
      if (!isWorking || !workingSince) return setElapsed(0);
      setElapsed(Math.max(0, Math.floor((Date.now() - new Date(workingSince).getTime()) / 1000)));
    };
    calculate();
    const timer = window.setInterval(calculate, 1000);
    return () => window.clearInterval(timer);
  }, [isWorking, workingSince]);

  useEffect(() => {
    setRcpOpen(false);
    setProfileOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const sendMessage = useMutation({
    mutationFn: () => post("/api/v1/messages", {
      recipientId: chatRecipient || null,
      body: chatBody.trim()
    }),
    onSuccess: async () => {
      setChatBody("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });

  const logout = async () => {
    await api("/api/v1/auth/logout", { method: "POST" });
    queryClient.clear();
    navigate("/app", { replace: true });
    window.location.reload();
  };

  const timeLabel = `${String(Math.floor(elapsed / 3600)).padStart(2, "0")}:${String(Math.floor((elapsed % 3600) / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
  const notificationCount = data.notifications.filter((item) => !item.read).length;
  const initials = data.auth.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
  const chatMessages = data.chatMessages.slice().sort((left, right) => left.createdAt.localeCompare(right.createdAt)).slice(-50);
  const chatUsers = data.users.filter((item) => item.active && item.id !== data.auth.id);
  const userName = (id: string) => id === data.auth.id ? "Ty" : data.users.find((item) => item.id === id)?.name ?? "Użytkownik";

  return (
    <div className="app-shell">
      <header className="topbar">
        <nav className="primary-nav" aria-label="Główna nawigacja">
          <NavLink to="/app" className="brand" aria-label="Mój ADIKAM - pulpit">
            <span className="brand-symbol"><span>A</span></span>
            <strong>Mój ADIKAM</strong>
          </NavLink>
          <div className={cx("primary-nav__links", mobileOpen && "is-open")}>
            {topLinks.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} className={({ isActive }) => cx("nav-link", isActive && "is-active")}>
                <Icon size={17} /><span>{label}</span>
              </NavLink>
            ))}
            <div className="menu-anchor" ref={rcpRef}>
              <button className={cx("nav-link", location.pathname.startsWith("/rcp/") && "is-active")} onClick={() => setRcpOpen((value) => !value)} aria-expanded={rcpOpen}>
                <Clock3 size={17} /><span>RCP</span><ChevronDown size={14} />
              </button>
              {rcpOpen ? <div className="dropdown-menu dropdown-menu--nav">{rcpLinks.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to}><Icon size={17} /><span>{label}</span></NavLink>)}</div> : null}
            </div>
          </div>
        </nav>

        <div className="topbar__actions">
          <button
            className={cx("virtual-clock", isWorking && "is-running")}
            onClick={() => void virtualPunch.punch()}
            disabled={virtualPunch.pending || virtualPunch.refreshFailed || (!virtualPunch.available && !virtualPunch.uncertain)}
            aria-label={virtualPunch.uncertain ? "Ponów tę samą próbę RCP" : isWorking ? "Zakończ pracę przez Wirtualne RCP" : "Rozpocznij pracę przez Wirtualne RCP"}
            title={virtualPunch.uncertain ? "Sprawdź wynik poprzedniej próby" : data.selfPunch?.reason || (isWorking ? "Zakończ pracę — online" : "Rozpocznij pracę — online")}
          >
            <span className="virtual-clock__icon">{isWorking ? <Square size={15} fill="currentColor" /> : <Play size={16} fill="currentColor" />}</span>
            <span className="virtual-clock__label">{virtualPunch.uncertain ? "Ponów tę samą próbę" : "Wirtualne RCP"}<small>{virtualPunch.pending ? "Sprawdzanie…" : isWorking ? "Czas pracy · online" : "Online"}</small></span>
            <strong>{timeLabel}</strong>
          </button>
          <IconButton label="Pomoc"><CircleHelp size={19} /></IconButton>
          <NavLink className="icon-link" to="/powiadomienia" aria-label={`Powiadomienia: ${notificationCount}`} title="Powiadomienia">
            <Bell size={19} />{notificationCount ? <span>{notificationCount}</span> : null}
          </NavLink>
          <div className="menu-anchor" ref={profileRef}>
            <button className="profile-button" onClick={() => setProfileOpen((value) => !value)} aria-expanded={profileOpen}>
              <Avatar initials={initials || "AD"} size="small" /><ChevronDown size={14} />
            </button>
            {profileOpen ? (
              <div className="dropdown-menu dropdown-menu--profile">
                <div className="profile-summary"><Avatar initials={initials || "AD"} /><div><strong>{data.auth.name}</strong><span>{data.auth.roleName}</span></div></div>
                <NavLink to="/samoobsluga"><UserRound size={17} /><span>Mój profil</span></NavLink>
                <NavLink to="/ustawienia"><Settings size={17} /><span>Ustawienia organizacji</span></NavLink>
                <NavLink to="/integracje"><ShieldCheck size={17} /><span>Integracje i API</span></NavLink>
                <button onClick={() => void logout()}><LogOut size={17} /><span>Wyloguj</span></button>
              </div>
            ) : null}
          </div>
          <IconButton label={mobileOpen ? "Zamknij menu" : "Otwórz menu"} className="mobile-menu-button" onClick={() => setMobileOpen((value) => !value)}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </IconButton>
        </div>
      </header>
      {virtualPunch.error || virtualPunch.uncertain || virtualPunch.success || !virtualPunch.available ? (
        <div className="page-content" aria-live="polite">
          {virtualPunch.error ? <p className="form-error" role="alert">{virtualPunch.error}</p> : null}
          {virtualPunch.success ? <p role="status">{virtualPunch.success}</p> : null}
          {virtualPunch.uncertain ? <p>Wynik odbicia jest niepewny. Ponów tę samą próbę przyciskiem Wirtualnego RCP. Przeładowanie strony, przejście do kiosku lub wylogowanie usuwa zapamiętaną próbę — przed nowym odbiciem sprawdź ewidencję.</p> : null}
          {!virtualPunch.available && data.selfPunch?.reason ? <p>Wirtualne RCP: {data.selfPunch.reason}</p> : null}
          {virtualPunch.uncertain ? <button onClick={() => void virtualPunch.punch()} disabled={virtualPunch.pending || virtualPunch.refreshFailed}>Ponów tę samą próbę RCP</button> : null}
          {virtualPunch.refreshFailed ? <button onClick={() => void virtualPunch.refreshState()} disabled={virtualPunch.pending}>Odśwież stan RCP</button> : null}
        </div>
      ) : null}
      <main className="app-main">{children}</main>
      <aside className={cx("chat-widget", chatOpen && "is-open")} aria-label="Wiadomości wewnętrzne">
        <button className="chat-widget__toggle" onClick={() => setChatOpen((value) => !value)} aria-expanded={chatOpen}>
          <MessageSquare size={18} /><strong>Wiadomości</strong><ChevronDown className={chatOpen ? "is-rotated" : ""} size={17} />
        </button>
        {chatOpen ? (
          <div className="chat-widget__panel">
            <label className="chat-widget__recipient">
              <span>Odbiorca</span>
              <select value={chatRecipient} onChange={(event) => setChatRecipient(event.target.value)}>
                <option value="">Cały zespół</option>
                {chatUsers.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
              </select>
            </label>
            <div className="chat-widget__messages" aria-live="polite">
              {chatMessages.length ? chatMessages.map((message) => (
                <div className={cx("chat-message", message.senderId === data.auth.id && "is-own")} key={message.id}>
                  <div><strong>{userName(message.senderId)}</strong><time>{new Intl.DateTimeFormat("pl-PL", { hour: "2-digit", minute: "2-digit" }).format(new Date(message.createdAt))}</time></div>
                  <p>{message.body}</p>
                </div>
              )) : <p className="chat-widget__empty">Brak wiadomości.</p>}
            </div>
            <form className="chat-widget__composer" onSubmit={(event) => { event.preventDefault(); if (chatBody.trim()) sendMessage.mutate(); }}>
              <textarea value={chatBody} onChange={(event) => setChatBody(event.target.value)} placeholder="Napisz wiadomość" rows={2} maxLength={4000} />
              <IconButton label="Wyślij wiadomość" type="submit" disabled={!chatBody.trim() || sendMessage.isPending}><Send size={17} /></IconButton>
            </form>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
